import './header.scss';

import React, {useCallback, useEffect} from 'react';
import {Navbar} from 'react-bootstrap';
import {useHistory} from 'react-router-dom';
import {useIsAnalysisPage, useIsLandingPage, useIsSearchPage} from './header-hooks';
import {useDispatch, useSelector} from 'react-redux';
import {SearchBar} from '../search/search-bar/searchBar';
import {SaveSearchComponent} from '../search/save-search/saveSearchComponent';
import {exportRequestedAction, fetchFacets, search, searchResetFacetsAction} from '../search/duck/search-actions';
import {
    useStateQuery,
    useStateQueryPending,
    useStateSavedSearches,
    useStateSavedSearchesSharedByOthers
} from '../search/search-hooks';
import {SearchPageType} from '../search/search-config';
import {ReportDropdown} from '../search/export/exportDropdownComponent';
import {SearchExportFormat} from '../search/search-types';
import {doHTMLExport, doJsonExport} from '../services/search-service';
import {SavedSearchHistory} from '../search/save-search/history/savedSearchHistoryComponent';
import {SearchInfo} from '../search/search-results/search-info/search-info';
import {fetchSavedSearchesIfNeeded} from '../search/save-search/duck/saveSearchComponent-actions';
import {useStateContainerName} from '../settings/duck/settings-hooks';
import {SavedSearchWithId} from '../search/save-search/duck/saveSearchComponent-types';
import {Option} from '../common/select/select';
import {useSharedSavedSearchesEnabled} from '../config/config-hooks';
import {ConditionalRender} from '../common/conditional-render/ConditionalRender';
import {UserMenu} from './userMenu';
import {HeaderMenu} from './headerMenu';

export const Header = (): JSX.Element => {
    const searchPageType = SearchPageType.BASIC;
    const dispatch = useDispatch();
    const query = useStateQuery();
    const retainFilterAcrossSearches = useSelector((state) => state.facetFilter.retainFilterAcrossSearches);
    const config = useSelector((state) => state.config);
    const landingPageEnabled = config.landingPage.enabled;
    const isLandingPage = useIsLandingPage() && landingPageEnabled;
    const isSearchPage = useIsSearchPage();
    const isAnalysisPage = useIsAnalysisPage();
    const {push} = useHistory();
    const history = useHistory();
    const queryPending = useStateQueryPending();
    const logoHibu = isLandingPage ? process.env.PUBLIC_URL + '/images/logo_start.svg' : process.env.PUBLIC_URL + '/images/logo_search.svg';
    const containerName = useStateContainerName();
    const savedSearches = useStateSavedSearches();
    const savedSearchesSharedByOthers = useStateSavedSearchesSharedByOthers();
    const savedSearchesSharedByOthersEnabled = useSharedSavedSearchesEnabled();

    useEffect(() => {
        dispatch(fetchSavedSearchesIfNeeded(searchPageType));
    }, [searchPageType, dispatch, containerName]);

    const triggerSearch = useCallback((searchExpression?: string) => {
            if (!retainFilterAcrossSearches) {
                dispatch(searchResetFacetsAction(searchPageType));
            }
            dispatch(search(searchPageType, history, searchExpression));
            dispatch(fetchFacets(searchPageType, searchExpression));
        },
        [dispatch, history, retainFilterAcrossSearches, searchPageType]
    );

    const onReportDropdownSelect = useCallback(
        (reportFormat: SearchExportFormat) => {
            dispatch(exportRequestedAction(searchPageType, reportFormat));

            if (reportFormat === SearchExportFormat.HTML) {
                dispatch(doHTMLExport(searchPageType));
            } else {
                dispatch(doJsonExport(searchPageType));
            }
        },
        [dispatch, searchPageType]
    );

    const navigateToHome = () => push('/');


    function mapToDistinctOptionsFromMultipleLists(...savedSearchLists: (SavedSearchWithId[] | undefined)[]): Option[] {
        const allSavedSearches = savedSearchLists.flat().filter((search): search is SavedSearchWithId => search !== undefined);
        const uniqueFolders = new Set(allSavedSearches.map(search => search.folder));
        return Array.from(uniqueFolders).map(folder => ({
            value: folder,
            label: folder
        }));
    }

    const folders = mapToDistinctOptionsFromMultipleLists(savedSearches, savedSearchesSharedByOthers);


    function renderHeader(): JSX.Element {
        return (
            <Navbar>
                <Navbar.Brand>
                    <ConditionalRender shouldRender={config.logoUrl !== undefined}>
                        <a href={config.logoUrl}>
                            <img id={'logo'} src={logoHibu} alt={'Hibu'}/>
                        </a>
                    </ConditionalRender>
                    <ConditionalRender shouldRender={config.logoUrl === undefined}>
                        <img id={'logo'} src={logoHibu} alt={'Hibu'} onClick={navigateToHome}/>
                    </ConditionalRender>
                </Navbar.Brand>
                <div id="header-search" className={!isSearchPage ? 'header-search-proxy' : ''}>
                    <div id="search-bar-and-sources">
                        <SearchBar
                            placeholder={'search.label.placeholder'}
                            onSubmit={triggerSearch}
                            initialValue={String(query)}
                        />
                        <ConditionalRender shouldRender={!queryPending}>
                            <SearchInfo/>
                        </ConditionalRender>
                    </div>
                    <SaveSearchComponent folderList={folders}
                                         savedSearchesSharedByOthersEnabled={savedSearchesSharedByOthersEnabled}/>
                    <div id="search-buttons">
                        <SavedSearchHistory savedSearches={savedSearches}
                                            savedSearchesSharedByOthers={savedSearchesSharedByOthers}
                                            savedSearchesSharedByOthersEnabled={savedSearchesSharedByOthersEnabled}/>
                        <ConditionalRender shouldRender={config.search.export && config.search.export.enabled}>
                            <ReportDropdown onSelect={onReportDropdownSelect}
                                            exportToggles={config.search.export.toggles}/>
                        </ConditionalRender>
                    </div>
                </div>
                <div id="header-right" className={isLandingPage ? 'dark' : 'light'}>
                    <UserMenu config={config} isLandingPage={isLandingPage}></UserMenu>
                    <HeaderMenu config={config} isSearchPage={isSearchPage}
                                isAnalysisPage={isAnalysisPage}></HeaderMenu>
                </div>
            </Navbar>
        );
    }

    return renderHeader();
};
